import { collection, addDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { EmailService } from '@municipality/firebase';

/**
 * Email Notification Service
 * 
 * Handles all email notifications for super admin user management
 * Sends emails via SendGrid Cloud Function and creates in-app notifications
 */

// ========================================
// TYPES
// ========================================

export interface AssignmentNotificationData {
  userId: string;
  userName: string;
  userEmail: string;
  municipalityName: string;
  municipalityCountry: string;
  accessLevel: string;
  grantedBy: string;
  grantedAt: string;
  expiresAt?: string;
  notes?: string;
}

export interface AdminCreatedNotificationData {
  newAdminName: string;
  newAdminEmail: string;
  newAdminRole: string;
  createdBy: string;
  createdAt: string;
}

export interface RegionManagerInvitationNotificationData {
  regionManagerName: string;
  regionManagerEmail: string;
  inviteeName: string;
  inviteeEmail: string;
  inviteeRole: string;
  assignedMunicipalities: string[];
  municipalityCount: number;
  invitedAt: string;
}

export interface InAppNotification {
  userId: string;
  title: string;
  message: string;
  type: 'assignment' | 'admin_created' | 'invitation' | 'access_revoked';
  metadata?: Record<string, unknown>;
  isRead: boolean;
  createdAt: Date;
}

// ========================================
// NOTIFICATION SERVICE
// ========================================

export class NotificationService {
  /**
   * Send assignment notification to user
   * Creates in-app notification and logs email data for SendGrid integration
   */
  static async sendAssignmentNotification(
    data: AssignmentNotificationData
  ): Promise<void> {
    try {
      // Create in-app notification (userId is Firebase Auth UID for path users/{userId}/notifications)
      await this.createInAppNotification({
        userId: data.userId,
        title: 'New municipality assignment',
        message: `You have been granted access to ${data.municipalityName}`,
        type: 'assignment',
        metadata: {
          municipalityName: data.municipalityName,
          accessLevel: data.accessLevel,
          grantedBy: data.grantedBy,
        },
        isRead: false,
        createdAt: new Date(),
      });

      // Send email via SendGrid Cloud Function (municipality-assignment template)
      try {
        const emailService = new EmailService();
        await emailService.sendEmail('municipality-assignment', {
          email: data.userEmail,
          fullName: data.userName,
          managerName: data.grantedBy,
          municipalityName: data.municipalityName,
          accessLevel: data.accessLevel,
        });
        console.log('Email sent: municipality assignment notification to', data.userEmail);
      } catch (emailError) {
        console.error('Failed to send assignment email (non-blocking):', emailError);
        // Don't throw - email failure shouldn't block the assignment operation
      }
    } catch (error) {
      console.error('Error sending assignment notification:', error);
      throw error;
    }
  }

  /**
   * Send admin creation notification to all Super Admins
   * Only triggers when Administrator creates another Administrator
   */
  static async sendAdminCreatedNotification(
    data: AdminCreatedNotificationData
  ): Promise<void> {
    try {
      // Fetch all Super Admin role users
      const superAdminsQuery = query(
        collection(db, 'users'),
        where('role', '==', 'super_admin'),
        where('superAdminRole', '==', 'super_admin')
      );
      
      const superAdminsSnapshot = await getDocs(superAdminsQuery);
      const superAdminEmails = superAdminsSnapshot.docs
        .map(doc => doc.data().email)
        .filter(Boolean);

      // Create in-app notifications for all Super Admins
      for (const superAdminDoc of superAdminsSnapshot.docs) {
        await this.createInAppNotification({
          userId: superAdminDoc.id,
          title: 'New administrator created',
          message: `${data.createdBy} created a new administrator: ${data.newAdminName}`,
          type: 'admin_created',
          metadata: {
            newAdminEmail: data.newAdminEmail,
            newAdminRole: data.newAdminRole,
            createdBy: data.createdBy,
          },
          isRead: false,
          createdAt: new Date(),
        });
      }

      // Send email to each Super Admin via SendGrid Cloud Function
      const emailService = new EmailService();
      for (const superAdminEmail of superAdminEmails) {
        try {
          await emailService.sendEmail('admin-created-notification', {
            email: superAdminEmail as string,
            newAdminName: data.newAdminName,
            newAdminEmail: data.newAdminEmail,
            newAdminRole: data.newAdminRole,
            createdBy: data.createdBy,
            createdAt: data.createdAt,
          });
          console.log('Email sent: admin created notification to', superAdminEmail);
        } catch (emailError) {
          console.error('Failed to send admin created email to', superAdminEmail, '(non-blocking):', emailError);
        }
      }
    } catch (error) {
      console.error('Error sending admin created notification:', error);
      throw error;
    }
  }

  /**
   * Send Region Manager invitation notification to all Super Admins
   * Triggers when Region Manager invites Content Manager or Support
   */
  static async sendRegionManagerInvitationNotification(
    data: RegionManagerInvitationNotificationData
  ): Promise<void> {
    try {
      // Fetch all Super Admin role users
      const superAdminsQuery = query(
        collection(db, 'users'),
        where('role', '==', 'super_admin'),
        where('superAdminRole', '==', 'super_admin')
      );
      
      const superAdminsSnapshot = await getDocs(superAdminsQuery);

      // Create in-app notifications for all Super Admins
      for (const superAdminDoc of superAdminsSnapshot.docs) {
        await this.createInAppNotification({
          userId: superAdminDoc.id,
          title: 'Region manager invitation',
          message: `${data.regionManagerName} invited ${data.inviteeName} as ${data.inviteeRole}`,
          type: 'invitation',
          metadata: {
            regionManagerEmail: data.regionManagerEmail,
            inviteeEmail: data.inviteeEmail,
            inviteeRole: data.inviteeRole,
            municipalityCount: data.municipalityCount,
          },
          isRead: false,
          createdAt: new Date(),
        });
      }

      // Send email to each Super Admin via SendGrid Cloud Function
      // Uses dedicated template with fullName, ManagerName, newUserFullName, invitedAt
      const emailService = new EmailService();
      for (const superAdminDoc of superAdminsSnapshot.docs) {
        const superAdminData = superAdminDoc.data();
        const superAdminEmail = superAdminData.email as string | undefined;
        if (!superAdminEmail) continue;
        const fullName = superAdminData.fullName as string | undefined ||
          superAdminData.displayName as string | undefined ||
          [superAdminData.name, superAdminData.surname].filter(Boolean).join(' ').trim() ||
          superAdminEmail;
        try {
          await emailService.sendEmail('region-manager-invite-super-admin', {
            email: superAdminEmail,
            fullName,
            ManagerName: data.regionManagerName,
            newUserFullName: data.inviteeName,
            invitedAt: data.invitedAt,
          });
          console.log('Email sent: region manager invitation notification to', superAdminEmail);
        } catch (emailError) {
          console.error('Failed to send region manager invitation email to', superAdminEmail, '(non-blocking):', emailError);
        }
      }
    } catch (error) {
      console.error('Error sending Region Manager invitation notification:', error);
      throw error;
    }
  }

  /**
   * Create in-app notification document
   * Stored in /users/{userId}/notifications subcollection
   */
  static async createInAppNotification(
    notification: InAppNotification
  ): Promise<void> {
    try {
      await addDoc(
        collection(db, 'users', notification.userId, 'notifications'),
        {
          ...notification,
          createdAt: Timestamp.fromDate(notification.createdAt),
        }
      );
    } catch (error) {
      console.error('Error creating in-app notification:', error);
      // Don't throw - notification failure shouldn't block main operation
    }
  }

  /**
   * Send access revocation notification
   */
  static async sendAccessRevokedNotification(
    userEmail: string,
    userName: string,
    municipalityName: string,
    revokedBy: string
  ): Promise<void> {
    try {
      // Access revocation uses the assignment notification template with revocation context
      try {
        const emailService = new EmailService();
        await emailService.sendEmail('assignment-notification', {
          email: userEmail,
          userName,
          municipalityName,
          municipalityCountry: '',
          accessLevel: 'Revoked',
          grantedBy: revokedBy,
          grantedAt: new Date().toISOString(),
          notes: `Your access to ${municipalityName} has been revoked.`,
        });
        console.log('Email sent: access revoked notification to', userEmail);
      } catch (emailError) {
        console.error('Failed to send access revoked email (non-blocking):', emailError);
      }
    } catch (error) {
      console.error('Error sending access revoked notification:', error);
      // Don't throw - notification failure shouldn't block revocation
    }
  }

  /**
   * Send assignment renewal notification
   */
  static async sendAssignmentRenewedNotification(
    userEmail: string,
    userName: string,
    municipalityName: string,
    renewedBy: string,
    newExpirationDate?: string
  ): Promise<void> {
    try {
      // Assignment renewal uses the assignment notification template with renewal context
      try {
        const emailService = new EmailService();
        await emailService.sendEmail('assignment-notification', {
          email: userEmail,
          userName,
          municipalityName,
          municipalityCountry: '',
          accessLevel: 'Renewed',
          grantedBy: renewedBy,
          grantedAt: new Date().toISOString(),
          expiresAt: newExpirationDate || 'No expiration',
          notes: `Your access to ${municipalityName} has been renewed.`,
        });
        console.log('Email sent: assignment renewed notification to', userEmail);
      } catch (emailError) {
        console.error('Failed to send renewal email (non-blocking):', emailError);
      }
    } catch (error) {
      console.error('Error sending assignment renewed notification:', error);
      // Don't throw - notification failure shouldn't block renewal
    }
  }
}
